import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useT } from "../i18n/LanguageContext.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfigPage() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("lda");
  const fileRef = useRef(null);
  const { t } = useT();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/config`);
      setConfig(r.data);
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const flash = (msg, isErr = false) => {
    if (isErr) setError(msg);
    else setStatus(msg);
    setTimeout(() => {
      setStatus(null);
      setError(null);
    }, 3500);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/config`, { config });
      flash(t("cfg.saved"));
    } catch (e) {
      flash(e.response?.data?.detail || String(e.message || e), true);
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/config/reset`);
      setConfig(r.data.config);
      flash(t("cfg.resetDone"));
    } catch (e) {
      flash(String(e.message || e), true);
    } finally {
      setBusy(false);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sepsis_config.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onExcelUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/config/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConfig(r.data);
      flash(t("cfg.extracted"));
    } catch (err) {
      flash(err.response?.data?.detail || String(err.message || err), true);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!config) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="config-loading">
        <div className="data-label">{t("cfg.loading")}</div>
      </div>
    );
  }

  const updateLdaCoef = (key, idx, value) => {
    const v = Number(value);
    setConfig((prev) => {
      const next = structuredClone(prev);
      next.lda.coefficients[key][idx] = Number.isFinite(v) ? v : value;
      return next;
    });
  };

  const updateIntercept = (idx, value) => {
    const v = Number(value);
    setConfig((prev) => {
      const next = structuredClone(prev);
      next.lda.intercept[idx] = Number.isFinite(v) ? v : value;
      return next;
    });
  };

  const updateThreshold = (idx, field, value) => {
    setConfig((prev) => {
      const next = structuredClone(prev);
      if (field === "threshold") {
        const v = Number(value);
        next.risk_classes[idx].threshold = Number.isFinite(v) ? v : value;
      } else {
        next.risk_classes[idx][field] = value;
      }
      return next;
    });
  };

  const updateRuleProb = (ruleIdx, classIdx, value) => {
    const v = Number(value);
    setConfig((prev) => {
      const next = structuredClone(prev);
      next.decision_tree.rules[ruleIdx].probabilities[classIdx] = Number.isFinite(v) ? v : value;
      return next;
    });
  };

  const updateRuleCondition = (ruleIdx, condIdx, field, value) => {
    setConfig((prev) => {
      const next = structuredClone(prev);
      const c = next.decision_tree.rules[ruleIdx].conditions[condIdx];
      if (field === "threshold") {
        const v = Number(value);
        c.threshold = Number.isFinite(v) ? v : value;
      } else {
        c[field] = value;
      }
      return next;
    });
  };

  const isUsersTab = tab === "users";
  const isTokensTab = tab === "apitokens";

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="config-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="data-label mb-2">{t("cfg.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("cfg.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t("cfg.description")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            data-testid="input-excel-upload"
            onChange={onExcelUpload}
            className="hidden"
            id="excel-upload"
          />
          <label
            htmlFor="excel-upload"
            data-testid="btn-import-excel"
            className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50 cursor-pointer"
          >
            {t("cfg.import")}
          </label>
          <button
            onClick={exportJson}
            data-testid="btn-export-json"
            className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50"
          >
            {t("cfg.export")}
          </button>
          <button
            onClick={resetDefaults}
            disabled={busy}
            data-testid="btn-reset-defaults"
            className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            {t("cfg.reset")}
          </button>
          {!isUsersTab && !isTokensTab && (
            <button
              onClick={save}
              disabled={busy}
              data-testid="btn-save-config"
              className="px-4 py-2 text-xs font-mono bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? t("cfg.saving") : t("cfg.save")}
            </button>
          )}
        </div>
      </div>

      {status && (
        <div className="mb-4 p-3 border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm" data-testid="status-banner">
          {status}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="error-banner">
          {error}
        </div>
      )}

      <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          { k: "lda", label: t("cfg.tab.lda") },
          { k: "tree", label: `${t("cfg.tab.tree")} (${config.decision_tree.rules.length})` },
          { k: "thresholds", label: t("cfg.tab.thresholds") },
          { k: "users", label: `${t("cfg.tab.users")} (${(config.allowed_emails || []).length})` },
          { k: "apitokens", label: t("cfg.tab.apitokens") },
        ].map((tab2) => (
          <button
            key={tab2.k}
            onClick={() => setTab(tab2.k)}
            data-testid={`tab-${tab2.k}`}
            className={[
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
              tab === tab2.k
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900",
            ].join(" ")}
          >
            {tab2.label}
          </button>
        ))}
      </div>

      {tab === "lda" && (
        <LdaTable config={config} onCoef={updateLdaCoef} onIntercept={updateIntercept} t={t} />
      )}
      {tab === "tree" && (
        <TreeEditor config={config} onProb={updateRuleProb} onCond={updateRuleCondition} t={t} />
      )}
      {tab === "thresholds" && <ThresholdEditor config={config} onChange={updateThreshold} t={t} />}
      {tab === "users" && <UsersEditor onSaved={load} flash={flash} t={t} />}
      {tab === "apitokens" && <ApiTokensEditor flash={flash} t={t} allowedEmails={config.allowed_emails || []} />}
    </div>
  );
}

function LdaTable({ config, onCoef, onIntercept, t }) {
  const classes = config.lda.classes;
  return (
    <div className="border border-slate-200 overflow-x-auto" data-testid="lda-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-left">
            <th className="px-4 py-3 data-label">{t("cfg.lda.variable")}</th>
            {classes.map((c, i) => (
              <th key={c} className="px-4 py-3 data-label text-right">
                c{i} · {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="row-hover border-b border-slate-200 bg-slate-50/50">
            <td className="px-4 py-2 font-medium">{t("cfg.lda.intercept")}</td>
            {classes.map((_, i) => (
              <td key={i} className="px-2 py-1 text-right">
                <input
                  type="number"
                  step="any"
                  data-testid={`intercept-${i}`}
                  value={config.lda.intercept[i]}
                  onChange={(e) => onIntercept(i, e.target.value)}
                  className="w-full text-right font-mono tabular-nums text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
            ))}
          </tr>
          {config.variables.map((v) => (
            <tr key={v.key} className="row-hover border-b last:border-b-0 border-slate-100">
              <td className="px-4 py-2">
                <div className="font-mono text-[12px] text-slate-900">{v.key}</div>
                <div className="text-[11px] text-slate-500">{v.label}</div>
              </td>
              {classes.map((_, i) => (
                <td key={i} className="px-2 py-1 text-right">
                  <input
                    type="number"
                    step="any"
                    data-testid={`coef-${v.key}-${i}`}
                    value={config.lda.coefficients[v.key]?.[i] ?? 0}
                    onChange={(e) => onCoef(v.key, i, e.target.value)}
                    className="w-full text-right font-mono tabular-nums text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TreeEditor({ config, onProb, onCond, t }) {
  return (
    <div className="space-y-4" data-testid="tree-editor">
      {config.decision_tree.rules.map((rule, ri) => (
        <div key={rule.node_id + ri} className="border border-slate-200">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
            <div>
              <span className="data-label">{t("cfg.tree.node")}</span>{" "}
              <span className="font-mono text-sm text-slate-900">{rule.node_id}</span>
              <span className="ml-3 data-label">{t("cfg.tree.count")}</span>{" "}
              <span className="font-mono text-xs text-slate-700">{rule.count}</span>
            </div>
            <div className="flex items-center gap-3">
              {rule.probabilities.map((p, ci) => (
                <div key={ci} className="flex items-center gap-1">
                  <span className="data-label">c{ci}</span>
                  <input
                    type="number"
                    step="any"
                    data-testid={`rule-${ri}-prob-${ci}`}
                    value={p}
                    onChange={(e) => onProb(ri, ci, e.target.value)}
                    className="w-20 font-mono tabular-nums text-xs text-right px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rule.conditions.map((c, ci) => (
              <div
                key={ci}
                className="flex items-center gap-2 border border-slate-200 px-3 py-2"
                data-testid={`rule-${ri}-cond-${ci}`}
              >
                <span className="data-label">{String(ci + 1).padStart(2, "0")}</span>
                <input
                  type="text"
                  value={c.variable}
                  onChange={(e) => onCond(ri, ci, "variable", e.target.value)}
                  className="flex-1 font-mono text-xs px-1 py-0.5 border-b border-transparent hover:border-slate-300 focus:border-slate-900 outline-none"
                />
                <select
                  value={c.operator}
                  onChange={(e) => onCond(ri, ci, "operator", e.target.value)}
                  className="font-mono text-xs px-1 py-0.5 border border-slate-200"
                >
                  {[">=", "<=", ">", "<", "=", "=="].map((op) => (
                    <option key={op}>{op}</option>
                  ))}
                </select>
                <input
                  type="number"
                  step="any"
                  value={c.threshold}
                  onChange={(e) => onCond(ri, ci, "threshold", e.target.value)}
                  className="w-24 text-right font-mono text-xs px-1 py-0.5 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ThresholdEditor({ config, onChange, t }) {
  return (
    <div className="border border-slate-200 max-w-4xl" data-testid="threshold-editor">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-left">
            <th className="px-4 py-3 data-label">{t("cfg.thr.threshold")}</th>
            <th className="px-4 py-3 data-label">{t("cfg.thr.labelEn")}</th>
            <th className="px-4 py-3 data-label">{t("cfg.thr.labelIt")}</th>
            <th className="px-4 py-3 data-label">{t("cfg.thr.labelFr")}</th>
            <th className="px-4 py-3 data-label">{t("cfg.thr.labelEs")}</th>
          </tr>
        </thead>
        <tbody>
          {config.risk_classes.map((rc, i) => (
            <tr key={i} className="row-hover border-b last:border-b-0 border-slate-100">
              <td className="px-2 py-2">
                <input
                  type="number"
                  step="any"
                  data-testid={`threshold-${i}`}
                  value={rc.threshold}
                  onChange={(e) => onChange(i, "threshold", e.target.value)}
                  className="w-full text-right font-mono tabular-nums text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  value={rc.label_en}
                  data-testid={`label-en-${i}`}
                  onChange={(e) => onChange(i, "label_en", e.target.value)}
                  className="w-full text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  value={rc.label_it}
                  data-testid={`label-it-${i}`}
                  onChange={(e) => onChange(i, "label_it", e.target.value)}
                  className="w-full text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  value={rc.label_fr || ""}
                  data-testid={`label-fr-${i}`}
                  onChange={(e) => onChange(i, "label_fr", e.target.value)}
                  className="w-full text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  value={rc.label_es || ""}
                  data-testid={`label-es-${i}`}
                  onChange={(e) => onChange(i, "label_es", e.target.value)}
                  className="w-full text-sm px-2 py-1 border border-slate-200 focus:border-slate-900 outline-none"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsersEditor({ onSaved, flash, t }) {
  const [emails, setEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/config/allowed-emails`);
      setEmails(r.data.emails);
      setCurrentUser(r.data.current_user);
    } catch (e) {
      flash(e.response?.data?.detail || String(e.message || e), true);
    } finally {
      setLoading(false);
    }
  };

  const persist = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/config/allowed-emails`, { emails: next });
      setEmails(r.data.emails);
      flash(t("cfg.users.updated"));
      onSaved && onSaved();
    } catch (e) {
      flash(e.response?.data?.detail || String(e.message || e), true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addEmail = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      flash(t("cfg.errors.invalidEmail"), true);
      return;
    }
    if (emails.includes(e)) {
      flash(t("cfg.errors.dup"), true);
      return;
    }
    await persist([...emails, e]);
    setNewEmail("");
  };

  const removeEmail = async (e) => {
    if (e.toLowerCase() === (currentUser || "").toLowerCase()) {
      flash(t("cfg.errors.removeSelf"), true);
      return;
    }
    await persist(emails.filter((x) => x.toLowerCase() !== e.toLowerCase()));
  };

  if (loading) {
    return (
      <div className="data-label" data-testid="users-loading">
        {t("cfg.users.loading")}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8" data-testid="users-editor">
      <div className="border border-slate-200">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="data-label">{t("cfg.users.title")}</div>
          <div className="data-label" data-testid="users-count">
            {emails.length} {t("cfg.users.active")}
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {emails.map((e) => {
            const isMe = e.toLowerCase() === (currentUser || "").toLowerCase();
            return (
              <li
                key={e}
                className="px-5 py-3 flex items-center justify-between"
                data-testid={`user-row-${e}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border border-slate-300 flex items-center justify-center font-mono text-[11px] text-slate-700 uppercase">
                    {e.slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-mono text-sm text-slate-900">{e}</div>
                    {isMe && <div className="data-label">{t("cfg.users.you")}</div>}
                  </div>
                </div>
                <button
                  onClick={() => removeEmail(e)}
                  disabled={isMe || busy}
                  data-testid={`btn-remove-${e}`}
                  className="px-3 py-1.5 text-xs font-mono border border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-slate-300 disabled:hover:text-slate-700"
                >
                  {t("cfg.users.remove")}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="border border-slate-200 p-5 self-start">
        <div className="data-label mb-3">{t("cfg.users.invite")}</div>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">{t("cfg.users.help")}</p>
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addEmail();
          }}
          placeholder="name@domain.com"
          data-testid="input-new-email"
          className="w-full font-mono tabular-nums text-sm px-3 py-2 border border-slate-300 focus:border-slate-900 outline-none mb-3"
        />
        <button
          onClick={addEmail}
          disabled={busy || !newEmail.trim()}
          data-testid="btn-add-email"
          className="w-full px-4 py-2.5 text-sm font-medium bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? t("cfg.saving") : t("cfg.users.add")}
        </button>
        <div className="mt-5 pt-4 border-t border-slate-200 text-[11px] font-mono text-slate-500 leading-relaxed">
          {t("cfg.users.signedIn")}
          <div className="text-slate-900 mt-0.5" data-testid="current-user-email">
            {currentUser}
          </div>
        </div>
      </div>
    </div>
  );
}



function ApiTokensEditor({ flash, t, allowedEmails }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState(null); // { token, record }
  const [copied, setCopied] = useState(false);

  const [name, setName] = useState("");
  const [robotEmail, setRobotEmail] = useState("");
  const [ttl, setTtl] = useState("365"); // string keys: '30' | '90' | '365' | 'none'

  const sortedEmails = [...(allowedEmails || [])].sort();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tokens`);
      setItems(r.data.items || []);
    } catch (e) {
      flash(e.response?.data?.detail || String(e.message || e), true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-select the first allow-listed email so the form is ready to submit
    // without making the user click a dropdown that only has one entry.
    if (!robotEmail && sortedEmails.length > 0) {
      setRobotEmail(sortedEmails[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedEmails.length]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ttlDays = ttl === "none" ? null : Number(ttl);
      const r = await axios.post(`${API}/tokens`, {
        name: name.trim(),
        robot_email: robotEmail,
        ttl_days: ttlDays,
      });
      setMinted(r.data);
      setName("");
      // Refresh the list so the new token shows up.
      await load();
    } catch (e2) {
      flash(e2.response?.data?.detail || String(e2.message || e2), true);
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    if (!minted?.token) return;
    try {
      await navigator.clipboard.writeText(minted.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard blocked – noop
    }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("tok.revoke.confirm"))) return;
    try {
      await axios.delete(`${API}/tokens/${id}`);
      await load();
    } catch (e) {
      flash(e.response?.data?.detail || String(e.message || e), true);
    }
  };

  const fmt = (iso) => {
    if (!iso) return t("tok.never");
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const TTL_OPTIONS = [
    { v: "30", k: "tok.create.ttl.30" },
    { v: "90", k: "tok.create.ttl.90" },
    { v: "365", k: "tok.create.ttl.365" },
    { v: "none", k: "tok.create.ttl.none" },
  ];

  const STATUS_STYLE = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    revoked: "bg-slate-100 text-slate-500 border-slate-200 line-through",
    expired: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div className="space-y-8" data-testid="api-tokens-editor">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{t("tok.title")}</h2>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">{t("tok.subtitle")}</p>
      </div>

      {minted && (
        <div className="border-2 border-amber-300 bg-amber-50 p-5" data-testid="minted-banner">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="data-label text-amber-900">{t("tok.minted.title")}</div>
              <div className="text-xs text-amber-800 mt-1 max-w-2xl">{t("tok.minted.warning")}</div>
            </div>
            <button
              type="button"
              onClick={() => setMinted(null)}
              data-testid="btn-minted-dismiss"
              className="px-3 py-1.5 text-xs font-mono border border-amber-400 hover:bg-amber-100"
            >
              {t("tok.minted.dismiss")}
            </button>
          </div>
          <textarea
            readOnly
            value={minted.token}
            data-testid="minted-token-value"
            onFocus={(e) => e.target.select()}
            className="w-full h-24 font-mono text-[11px] p-3 bg-white border border-amber-300 break-all resize-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={copyToken}
              data-testid="btn-minted-copy"
              className="px-3 py-1.5 text-xs font-mono bg-amber-700 text-white hover:bg-amber-800"
            >
              {copied ? t("tok.minted.copied") : t("tok.minted.copy")}
            </button>
            <span className="text-[11px] font-mono text-amber-900">
              {minted.record.name} · {minted.record.robot_email}
            </span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="border border-slate-200 p-5 space-y-4" data-testid="token-create-form">
        <div className="data-label">{t("tok.create.heading")}</div>
        <label className="block">
          <div className="data-label mb-1">{t("tok.create.name")}</div>
          <input
            type="text"
            required
            maxLength={128}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("tok.create.name.placeholder")}
            data-testid="input-token-name"
            className="w-full font-mono text-sm px-3 py-2 border border-slate-300 focus:border-slate-900 outline-none"
          />
        </label>
        <label className="block">
          <div className="data-label mb-1">{t("tok.create.email")}</div>
          {sortedEmails.length === 0 ? (
            <div className="text-xs text-amber-700 border border-amber-300 bg-amber-50 px-3 py-2" data-testid="email-empty-hint">
              {t("tok.create.email.empty")}
            </div>
          ) : (
            <select
              required
              value={robotEmail}
              onChange={(e) => setRobotEmail(e.target.value)}
              data-testid="select-token-robot"
              className="w-full font-mono text-sm px-3 py-2 border border-slate-300 focus:border-slate-900 outline-none bg-white"
            >
              {sortedEmails.map((em) => (
                <option key={em} value={em}>{em}</option>
              ))}
            </select>
          )}
          <div className="text-[11px] text-slate-500 mt-1.5">{t("tok.create.email.help")}</div>
        </label>
        <fieldset>
          <legend className="data-label mb-2">{t("tok.create.ttl")}</legend>
          <div className="flex flex-wrap gap-2">
            {TTL_OPTIONS.map((opt) => (
              <label
                key={opt.v}
                data-testid={`ttl-option-${opt.v}`}
                className={[
                  "px-3 py-1.5 text-xs font-mono border cursor-pointer",
                  ttl === opt.v
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="ttl"
                  value={opt.v}
                  checked={ttl === opt.v}
                  onChange={() => setTtl(opt.v)}
                  className="sr-only"
                />
                {t(opt.k)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="pt-2">
          <button
            type="submit"
            disabled={busy || sortedEmails.length === 0}
            data-testid="btn-submit-token"
            className="px-4 py-2 text-sm font-mono bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? t("tok.create.submitting") : t("tok.create.submit")}
          </button>
        </div>
      </form>

      <div>
        <div className="data-label mb-3">{t("tok.list.heading")}</div>
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="tokens-loading">…</div>
        ) : items.length === 0 ? (
          <div className="border border-slate-200 p-6 text-sm text-slate-500 text-center" data-testid="tokens-empty">
            {t("tok.list.empty")}
          </div>
        ) : (
          <div className="border border-slate-200 overflow-x-auto" data-testid="tokens-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-4 py-3 data-label">{t("tok.col.name")}</th>
                  <th className="px-4 py-3 data-label">{t("tok.col.robot")}</th>
                  <th className="px-4 py-3 data-label">{t("tok.col.created")}</th>
                  <th className="px-4 py-3 data-label">{t("tok.col.expires")}</th>
                  <th className="px-4 py-3 data-label">{t("tok.col.lastUsed")}</th>
                  <th className="px-4 py-3 data-label">{t("tok.col.status")}</th>
                  <th className="px-4 py-3 data-label"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const statusKey = `tok.status.${it.status}`;
                  return (
                    <tr key={it.id} className="border-b border-slate-100" data-testid={`token-row-${it.id}`}>
                      <td className="px-4 py-2 text-slate-900">{it.name}</td>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-700">{it.robot_email}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{fmt(it.created_at)}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                        {it.no_expiry ? <span className="italic text-slate-400">{t("tok.noExpiry")}</span> : fmt(it.expires_at)}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{fmt(it.last_used_at)}</td>
                      <td className="px-4 py-2">
                        <span
                          data-testid={`token-status-${it.id}`}
                          className={["inline-block px-2 py-0.5 text-[11px] font-mono border", STATUS_STYLE[it.status]].join(" ")}
                        >
                          {t(statusKey)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {it.status === "active" && (
                          <button
                            type="button"
                            onClick={() => revoke(it.id)}
                            data-testid={`btn-revoke-${it.id}`}
                            className="px-2 py-1 text-[11px] font-mono border border-red-300 text-red-700 hover:bg-red-50"
                          >
                            {t("tok.revoke")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
